import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Sparkles, FileText, Send, Share2, Loader2, CheckSquare, BookOpen, FileSpreadsheet, ClipboardList, HelpCircle } from "lucide-react";
import { AIGenerationLoader } from "@/components/dashboard/AIGenerationLoader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useCredits } from "@/hooks/useCredits";
import { CreditCostBadge } from "@/components/CreditGate";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import EmailResultsRenderer from "@/components/email-builder/EmailResultsRenderer";

const AUDIENCE_SIZES = [
  { value: "under-500", label: "Under 500" },
  { value: "500-2000", label: "500 – 2,000" },
  { value: "2000-10000", label: "2,000 – 10,000" },
  { value: "10000-50000", label: "10,000 – 50,000" },
  { value: "50000+", label: "50,000+" },
];

const LEAD_MAGNET_FORMATS = [
  { value: "checklist", label: "Checklist", icon: CheckSquare, desc: "Step-by-step actionable list" },
  { value: "cheat-sheet", label: "Cheat Sheet", icon: FileSpreadsheet, desc: "Quick-reference guide" },
  { value: "template", label: "Template / Swipe File", icon: ClipboardList, desc: "Fill-in-the-blank templates" },
  { value: "short-guide", label: "Short Guide (3-5 pages)", icon: BookOpen, desc: "In-depth mini guide" },
  { value: "quiz", label: "Quiz / Assessment", icon: HelpCircle, desc: "Interactive assessment outline" },
];

const PREVIEW_ITEMS = [
  { icon: FileText, title: "Lead Magnet", desc: "A checklist, guide, or template tailored to your niche — ready to offer as a free download", color: "text-primary" },
  { icon: Mail, title: "Opt-in Page Copy", desc: "Headline, subheadline, and bullet points you can paste straight into your landing page", color: "text-primary" },
  { icon: Send, title: "5-Email Welcome Sequence", desc: "Day 0 through Day 7 — pre-written emails with 3 subject line variations each for A/B testing", color: "text-primary" },
  { icon: Share2, title: "Promo Strategy", desc: "Social media posts designed to drive signups from your existing audience", color: "text-primary" },
];

const EmailListBuilder = () => {
  const [searchParams] = useSearchParams();
  const [niche, setNiche] = useState("");
  const [audienceSize, setAudienceSize] = useState("");
  const [audienceDescription, setAudienceDescription] = useState("");
  const [leadMagnetFormat, setLeadMagnetFormat] = useState("checklist");
  const [result, setResult] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const { consume } = useCredits();

  // Pre-fill from URL params (from Monetisation AI)
  useEffect(() => {
    const nicheParam = searchParams.get("niche");
    const audienceParam = searchParams.get("audience");
    if (nicheParam) setNiche(nicheParam);
    if (audienceParam) setAudienceDescription(audienceParam);
  }, [searchParams]);

  const handleGenerate = async () => {
    if (!niche.trim()) {
      toast.error("Please enter your niche");
      return;
    }

    const success = await consume("email-list-builder", "Email List Builder AI generation");
    if (!success) return;

    setIsGenerating(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("monetization-ai", {
        body: {
          type: "email-list-builder",
          params: {
            niche: niche.trim(),
            audienceSize: audienceSize || undefined,
            audienceDescription: audienceDescription.trim() || undefined,
            leadMagnetFormat,
          },
        },
      });

      if (error) throw error;
      setResult(data?.content || data?.result || "No result generated.");
      toast.success("Email list building system generated!");
    } catch (err: any) {
      console.error("Email List Builder error:", err);
      toast.error(err.message || "Failed to generate. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <Mail className="w-7 h-7 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">Email List Builder AI</h1>
          <p className="text-muted-foreground">Generate a complete list-building system in one click</p>
        </div>
        <CreditCostBadge action="email-list-builder" className="ml-auto" />
      </div>

      {/* Input Form */}
      <Card className="bg-card/50 border-border/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Your Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Niche */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Your Niche *</label>
            <Input
              placeholder="e.g. Personal finance for millennials, Home cooking for busy parents"
              value={niche}
              onChange={(e) => setNiche(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1.5">Be specific — "Vegan meal prep for working moms" works better than just "Food"</p>
          </div>

          {/* Audience Size */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Your Current Audience Size</label>
            <Select value={audienceSize} onValueChange={setAudienceSize}>
              <SelectTrigger>
                <SelectValue placeholder="Select your audience size" />
              </SelectTrigger>
              <SelectContent>
                {AUDIENCE_SIZES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1.5">Across all platforms — helps tailor your promo strategy</p>
          </div>

          {/* Audience Description */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">
              Describe Your Audience <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <Textarea
              placeholder="Who are they? What's their biggest problem? What do they want to achieve?&#10;&#10;e.g. 'Women aged 25-35 who want to start investing but feel overwhelmed by jargon'"
              value={audienceDescription}
              onChange={(e) => setAudienceDescription(e.target.value)}
              rows={4}
            />
            <p className="text-xs text-muted-foreground mt-1.5">The more detail you give, the more personalised your lead magnet and emails will be</p>
          </div>

          {/* Lead Magnet Format */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Lead Magnet Format</label>
            <ToggleGroup
              type="single"
              value={leadMagnetFormat}
              onValueChange={(v) => v && setLeadMagnetFormat(v)}
              className="flex flex-wrap justify-start gap-2"
            >
              {LEAD_MAGNET_FORMATS.map((fmt) => (
                <ToggleGroupItem
                  key={fmt.value}
                  value={fmt.value}
                  className="flex items-center gap-2 px-3 py-2 h-auto rounded-lg border border-border/50 data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary"
                >
                  <fmt.icon className="w-4 h-4 shrink-0" />
                  <span className="text-sm">{fmt.label}</span>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <p className="text-xs text-muted-foreground mt-1.5">
              {LEAD_MAGNET_FORMATS.find((f) => f.value === leadMagnetFormat)?.desc}
            </p>
          </div>

          <Button variant="hero" size="lg" className="w-full" onClick={handleGenerate} disabled={isGenerating || !niche.trim()}>
            {isGenerating ? (
              <><Loader2 className="w-5 h-5 animate-spin mr-2" />Generating…</>
            ) : (
              <><Sparkles className="w-5 h-5 mr-2" />Generate Email List System (2 Credits)</>
            )}
          </Button>
          {isGenerating && <AIGenerationLoader tool="email-funnel" />}
        </CardContent>
      </Card>

      {/* What You'll Get - Visual Preview */}
      {!result && (
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4">What You'll Get</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PREVIEW_ITEMS.map((item) => (
              <Card key={item.title} className="bg-card/40 border-border/40 hover:border-primary/30 transition-colors">
                <CardContent className="p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                    <item.icon className={`w-5 h-5 ${item.color}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-foreground mb-1">{item.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <Card className="bg-card/50 border-border/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Your Email List Building System
            </CardTitle>
          </CardHeader>
          <CardContent>
            <EmailResultsRenderer rawContent={result} />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmailListBuilder;
